/**
 * SIP Provider Component
 * Orchestrates the SIP connection and state
 */

import { useCallback, useRef, useState, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { SIP, UA_EVENT, CallEventHandler } from "./SipService";
import { useAppSelector } from "../../hooks";
import { selectSteContext } from "../../features/auth";
import { SipContext, type SipContextValue } from "./SipContext";

interface SipProviderProps {
  children: ReactNode;
}

export function SipProvider({ children }: SipProviderProps) {
  const sipRef = useRef<SIP | null>(null);
  const steContext = useAppSelector(selectSteContext);

  // State
  const [isConnected, setIsConnected] = useState(false);
  const [isCallInProgress, setIsCallInProgress] = useState(false);
  const [isCallRinging, setIsCallRinging] = useState(false);
  const [isMicrophoneGranted, setIsMicrophoneGranted] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Request microphone access
  const requestMicrophoneAccess = useCallback(async (): Promise<boolean> => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Browser doesn't support microphone access");
        return false;
      }

      await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsMicrophoneGranted(true);
      setError(null);
      return true;
    } catch (err: unknown) {
      console.error("Microphone access error:", err);
      setIsMicrophoneGranted(false);

      const error = err as Error & { name?: string };
      if (error.name === "NotFoundError") {
        setError(
          "No microphone found. Please connect a microphone and try again.",
        );
      } else if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        setError(
          "Microphone access denied. Please allow microphone access in your browser settings.",
        );
      } else if (error.name === "NotReadableError") {
        setError("Microphone is in use by another application.");
      } else {
        setError("Could not access microphone. Please check your device.");
      }
      return false;
    }
  }, []);

  // Connect to SIP server
  const connect = useCallback(() => {
    if (!steContext) {
      setError("Not logged in");
      return;
    }

    if (sipRef.current) {
      return; // Already connected
    }

    const incomingHandler = new CallEventHandler(
      () => {
        console.log("Incoming call progress");
        setIsCallRinging(true);
      },
      (e) => {
        console.error("Incoming call failed", e);
        setIsCallInProgress(false);
        setIsCallRinging(false);
      },
      () => {
        console.log("Incoming call ended");
        setIsCallInProgress(false);
        setIsCallRinging(false);
      },
      () => {
        console.log("Incoming call confirmed");
        setIsCallInProgress(true);
        setIsCallRinging(false);
      },
    );

    // Extract username and domain from SIP URI (e.g., sip:webrtc@connect.staging.callee.pro)
    const sipUsername = steContext.sipURI.replace(/^sip:/, "").split("@")[0];
    const sipBaseDomain = steContext.sipURI.split("@")[1];
    const wsUrl = `wss://${sipBaseDomain}:8089/ws`;

    setError(null); // Clear any previous errors

    console.log("[SipContext] Connecting to SIP server:", {
      sipURI: steContext.sipURI,
      sipUsername,
      sipBaseDomain,
      wsUrl,
    });

    const sip = new SIP({
      registrationURI: wsUrl,
      contactURI: steContext.sipURI,
      password: steContext.sipPassword,
      incomingCallHandler: incomingHandler,
      extra_headers: [],
    });

    sipRef.current = sip;
    sip.start();

    sip.on(UA_EVENT.CONNECTED, () => {
      console.log("[SipContext] WebSocket connected!");
      setIsConnected(true);
      setError(null);
    });

    sip.on(UA_EVENT.DISCONNECTED, () => {
      setIsConnected(false);
      setIsRegistered(false);
      setError("Failed to register with SIP server");
    });

    sip.on(UA_EVENT.REGISTERED, () => {
      console.log("SIP registered successfully");
      setIsRegistered(true);
      setError(null);
    });

    sip.on(UA_EVENT.REGISTRATION_FAILED, () => {
      console.error("SIP registration failed");
      setIsRegistered(false);
      setError("Failed to register with SIP server");
    });
  }, [steContext]);

  // Disconnect from SIP server
  const disconnect = useCallback(() => {
    sipRef.current?.stop();
    sipRef.current = null;
    setIsConnected(false);
    setIsRegistered(false);
    setIsCallInProgress(false);
    setIsCallRinging(false);
  }, []);

  // Make a call - uses serverSipURI from STE context and passes contact ID via header
  const makeCall = useCallback(
    (contactId: string) => {
      if (!sipRef.current) {
        setError("Not connected to SIP server");
        return;
      }

      if (!steContext?.serverSipURI) {
        setError("Extension SIP URI not available");
        return;
      }

      const callHandler = new CallEventHandler(
        () => {
          console.log("[SipContext] Call ringing");
          setIsCallRinging(true);
        },
        (e) => {
          console.log("[SipContext] Call failed", {
            originator: e.originator,
            message: e.message,
            cause: e.cause,
          });
          setIsCallInProgress(false);
          setIsCallRinging(false);
          setError(`Call failed: ${e.cause || "Unknown error"}`);
        },
        () => {
          console.log("[SipContext] Call ended");
          setIsCallInProgress(false);
          setIsCallRinging(false);
        },
        () => {
          console.log("[SipContext] Call confirmed");
          setIsCallInProgress(true);
          setIsCallRinging(false);
          setError(null);
        },
      );

      // Build the x-eager-gather-queue header with the contact ID as a JSON array
      const queueHeader = `X-CONTACT-ID: ${JSON.stringify([contactId])}`;

      console.log(
        "[SipContext] Making call to:",
        steContext.serverSipURI,
        "with header:",
        queueHeader,
      );

      sipRef.current.call(steContext.serverSipURI, callHandler, [queueHeader]);
    },
    [steContext],
  );

  // End current call
  const endCall = useCallback(() => {
    console.log("[SipContext] endCall invoked!", new Error().stack);
    const session = sipRef.current?.getCurrentSession();
    if (session) {
      try {
        session.disconnect();
      } catch (error) {
        console.warn("[SipContext] Error ending call:", error);
      }
    }
    setIsCallInProgress(false);
    setIsCallRinging(false);
  }, []);

  // Send DTMF digit
  const sendDTMF = useCallback((digit: string) => {
    sipRef.current?.getCurrentSession()?.sendDTMF(digit);
  }, []);

  // Check microphone permission status on mount
  useEffect(() => {
    const checkMicrophonePermission = async () => {
      try {
        // Check if permissions API is available
        if (navigator.permissions && navigator.permissions.query) {
          const result = await navigator.permissions.query({
            name: "microphone" as PermissionName,
          });
          if (result.state === "granted") {
            setIsMicrophoneGranted(true);
          } else if (result.state === "denied") {
            setIsMicrophoneGranted(false);
          }
          // 'prompt' state means user hasn't decided yet, leave as false
        }
      } catch (_error) {
        // Permissions API not supported, will need to request explicitly
        console.log("[SipContext] Permissions API not available");
      }
    };
    checkMicrophonePermission();
  }, []);

  // Auto-connect when STE context becomes available is removed
  // to prevent SIP registration right after background login.
  // Connection is now triggered from useHomeViewModel instead.

  // Track call state in refs for cleanup
  const isCallActiveRef = useRef(false);
  useEffect(() => {
    isCallActiveRef.current = isCallInProgress || isCallRinging;
  }, [isCallInProgress, isCallRinging]);

  // Cleanup on unmount - but don't terminate active calls
  // This prevents StrictMode double-mounting from cancelling calls
  useEffect(() => {
    return () => {
      // Only cleanup if there's no active call
      if (!isCallActiveRef.current) {
        console.log("[SipContext] Cleanup: stopping SIP (no active call)");
        sipRef.current?.stop();
      } else {
        console.log("[SipContext] Cleanup: skipping SIP stop (call active)");
      }
    };
  }, []);

  const value = useMemo<SipContextValue>(
    () => ({
      // State
      isConnected,
      isCallInProgress,
      isCallRinging,
      isMicrophoneGranted,
      isRegistered,

      error,

      // Actions
      connect,
      disconnect,
      makeCall,
      endCall,
      sendDTMF,
      requestMicrophoneAccess,
      clearError,
    }),
    [
      isConnected,
      isCallInProgress,
      isCallRinging,
      isMicrophoneGranted,
      isRegistered,
      error,
      connect,
      disconnect,
      makeCall,
      endCall,
      sendDTMF,
      requestMicrophoneAccess,
      clearError,
    ],
  );

  return <SipContext.Provider value={value}>{children}</SipContext.Provider>;
}
